import { useEffect, useState } from "react";
import { BellRing, Share } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { pushStatus, turnOffPush, turnOnPush } from "@/lib/push";

const btn = "px-3 py-1.5 rounded-full text-xs font-medium transition-colors disabled:opacity-50";

const Explain = ({ status }) => {
  if (status === "ios-install") return (
    <ol className="mt-3 text-xs text-slate-300 space-y-1 list-decimal list-inside" data-testid="push-ios-steps">
      <li>Tap <Share size={12} className="inline -mt-0.5" /> Share at the bottom of Safari</li>
      <li>Choose <b>Add to Home Screen</b></li>
      <li>Open the Bucketlist from your Home Screen and come back here</li>
    </ol>
  );
  if (status === "denied") return <p className="mt-3 text-xs text-amber-300" data-testid="push-denied">Notifications are blocked. Allow them for this site in your phone or browser settings.</p>;
  if (status === "unsupported") return <p className="mt-3 text-xs text-slate-400" data-testid="push-unsupported">This browser can't show notifications. Try Chrome, Edge or Safari.</p>;
  return null;
};

export const PushCard = ({ compact }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => pushStatus().then(setStatus).catch(() => setStatus("unsupported"));
  useEffect(() => { refresh(); }, []);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Something went wrong"); }
    setBusy(false);
    refresh();
  };
  if (!status || (compact && status === "on")) return null;
  return (
    <section className={compact ? "px-4 py-3 border-b border-white/[0.06] bg-sky-400/[0.05]" : "rounded-xl border border-white/10 bg-white/[0.03] p-4"} data-testid={compact ? "push-prompt" : "push-card"}>
      <div className="flex items-center gap-2">
        <BellRing size={16} className="text-sky-300" />
        <h3 className="font-display font-semibold text-sm">Phone notifications</h3>
        <span className={`ml-auto text-[11px] font-mono ${status === "on" ? "text-emerald-300" : "text-slate-500"}`} data-testid="push-status">{status === "on" ? "On for this device" : "Off"}</span>
      </div>
      <p className="text-xs text-slate-400 mt-1.5">Get told when a friend is somewhere on your list, when your trips overlap, and when you know people where you're going.</p>
      <Explain status={status} />
      <div className="flex flex-wrap gap-2 mt-3">
        {status === "off" && <button disabled={busy} onClick={() => run(turnOnPush, "Notifications are on for this device")} className={`${btn} bg-sky-400 text-[#06121c] hover:bg-sky-300`} data-testid="push-turn-on">Turn on notifications</button>}
        {status === "on" && <>
          <button disabled={busy} onClick={() => run(api.pushTest, "Test sent — check your notifications")} className={`${btn} bg-white/10 hover:bg-white/15`} data-testid="push-test">Send me a test</button>
          <button disabled={busy} onClick={() => run(turnOffPush, "Notifications off for this device")} className={`${btn} border border-white/15 text-slate-300 hover:bg-white/5`} data-testid="push-turn-off">Turn off</button>
        </>}
      </div>
    </section>
  );
};
